export type FeedMode =
  | "realtime"
  | "delayed"
  | "historical"
  | "derived"
  | "mixed"
  | "unknown";

export type HealthTone =
  | "healthy"
  | "degraded"
  | "stale"
  | "warming"
  | "unavailable"
  | "unknown";

export type ChainStatus = "live" | "closed" | "stale" | "warming";

export function feedMode(source: string | null | undefined): FeedMode {
  const normalized = source?.trim().toLowerCase();
  if (!normalized) return "unknown";
  if (normalized.includes("delayed")) return "delayed";
  if (normalized.includes("historical")) return "historical";
  if (normalized.includes("derived")) return "derived";
  if (normalized === "mixed") return "mixed";
  if (
    normalized.endsWith("_live") ||
    normalized === "iex" ||
    normalized === "cboe_one" ||
    normalized.startsWith("nasdaq_basic")
  ) {
    return "realtime";
  }
  return "unknown";
}

export function feedModeLabel(source: string | null | undefined): string {
  switch (feedMode(source)) {
    case "realtime":
      return "REALTIME";
    case "delayed":
      return "15-MIN DELAY";
    case "historical":
      return "HISTORICAL";
    case "derived":
      return "CALCULATED";
    case "mixed":
      return "MIXED SOURCES";
    case "unknown":
      return "SOURCE PENDING";
  }
}

export function chainStatusLabel(
  status: ChainStatus | null | undefined,
): string {
  switch (status) {
    case "live":
      return "LIVE";
    case "closed":
      return "CLOSED";
    case "stale":
      return "STALE";
    case "warming":
    default:
      return "WARMING";
  }
}

export function chainStatusTone(
  status: ChainStatus | null | undefined,
): HealthTone | "closed" {
  switch (status) {
    case "live":
      return "healthy";
    case "closed":
      return "closed";
    case "stale":
      return "stale";
    case "warming":
    default:
      return "warming";
  }
}

export function formatMarketTimestamp(value: string | null | undefined): string {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  });
}

export function providerLabel(source: string | null | undefined): string {
  if (!source) return "DATABENTO";
  const normalized = source.trim().toLowerCase();
  if (normalized.startsWith("databento_equs")) {
    return "DATABENTO EQUS MINI (CONSOLIDATED)";
  }
  if (normalized.startsWith("databento_equities")) {
    return "DATABENTO EQUS MINI (CONSOLIDATED)";
  }
  if (normalized.startsWith("databento_opra")) return "DATABENTO OPRA";
  if (normalized.startsWith("databento_options")) return "DATABENTO OPRA";
  if (normalized === "databento_historical") return "DATABENTO HISTORICAL";
  if (normalized === "databento_derived") return "DATABENTO DERIVED";
  return source.replaceAll("_", " ").toUpperCase();
}

export function healthTone(status: string | null | undefined): HealthTone {
  const normalized = status?.trim().toLowerCase();
  if (!normalized) return "unknown";
  if (normalized.includes("stale")) return "stale";
  if (normalized.includes("degrad")) return "degraded";
  if (
    normalized === "ready" ||
    normalized === "healthy" ||
    normalized === "ok" ||
    normalized === "live"
  ) {
    return "healthy";
  }
  if (
    normalized.includes("warm") ||
    normalized.includes("start") ||
    normalized.includes("pending")
  ) {
    return "warming";
  }
  if (
    normalized.includes("unavailable") ||
    normalized.includes("not_configured") ||
    normalized.includes("offline") ||
    normalized.includes("error") ||
    normalized.includes("fail")
  ) {
    return "unavailable";
  }
  return "unknown";
}

export function healthStatusLabel(status: string | null | undefined): string {
  return status?.trim()
    ? status.trim().replaceAll("_", " ").toUpperCase()
    : "STATUS PENDING";
}
